import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Reviews from "@/components/Reviews";
import WishlistButton from "@/components/WishlistButton";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Ruler, MessageCircle, Check, ArrowRight, Truck, RotateCcw, ShieldCheck, Feather, Wind, Footprints, Layers, Gauge, Sparkles } from "lucide-react";
import { useAskSpotter } from "@/context/AskSpotterContext";
import { viewItem, addToCartEvent } from "@/lib/analytics";
import { COMPANY } from "@/config/company";
import SizeGuide from "@/components/SizeGuide";
import { chartKindFor, genderFromDepartment } from "@/config/sizeCharts";

// Icons cycle to give the benefit cards visual variety (Decathlon-style).
const BENEFIT_ICONS = [Feather, Wind, Footprints, Layers, Gauge, Sparkles];

// Metro pincode prefixes get the faster window. This is a client-side estimate;
// it is structured to be replaced by a real /delivery/estimate call (Shiprocket
// serviceability) without changing the surrounding UI.
const METRO_PREFIX = ["11", "40", "41", "56", "60", "70", "50", "38", "12", "20"];
function estimateDelivery(pin) {
  const p = String(pin || "").replace(/\D/g, "");
  if (p.length !== 6) return null;
  const metro = METRO_PREFIX.includes(p.slice(0, 2));
  return { serviceable: true, min: metro ? 3 : 5, max: metro ? 5 : 8, cod: !p.startsWith("19") };
}

// The section rail below the hero.
const SECTIONS = [
  { id: "details", label: "The details" },
  { id: "sizefit", label: "Size & fit" },
  { id: "inspection", label: "Inspection" },
  { id: "reviews", label: "Reviews" },
  { id: "maker", label: "The maker" },
  { id: "shipping", label: "Shipping & returns" },
];

function SectionRail({ active, onJump }) {
  return (
    <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-y border-foreground/15 -mx-6 md:mx-0 px-6 md:px-0">
      <div className="flex gap-6 md:gap-9 overflow-x-auto no-scrollbar py-4">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => onJump(s.id)}
            className={`whitespace-nowrap font-mono text-[10px] uppercase tracking-widest transition-colors ${active === s.id ? "text-[hsl(var(--accent))]" : "text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Spec({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className="grid grid-cols-[130px_1fr] gap-5 py-3.5 border-b border-foreground/12">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("");
  const [colour, setColour] = useState("");
  const [img, setImg] = useState(0);
  const [pin, setPin] = useState(() => { try { return localStorage.getItem("ts_pincode") || ""; } catch { return ""; } });
  const [delivery, setDelivery] = useState(null);
  const [active, setActive] = useState("details");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const { openFor } = useAskSpotter();
  const nav = useNavigate();
  const sectionRefs = useRef({});

  useEffect(() => {
    setP(null); setRelated([]); setDelivery(null); setImg(0);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setSize(r.data.sizes?.[0] || "");
      setColour(r.data.colors?.[0] || "");
      viewItem(r.data);
      api.get("/products", { params: { category: r.data.category } })
        .then(({ data }) => setRelated((data || []).filter((x) => x.id !== r.data.id).slice(0, 4)))
        .catch(() => setRelated([]));
    }).catch(() => {});
  }, [id]);

  // Re-run the saved pincode estimate once the product is loaded.
  useEffect(() => { if (p && pin) setDelivery(estimateDelivery(pin)); }, [p]); // eslint-disable-line

  // Lightweight scrollspy for the section rail.
  useEffect(() => {
    if (!p) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.dataset.section); });
    }, { rootMargin: "-45% 0px -50% 0px" });
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [p]);

  const cert = p?.spotted_certificate;
  const checks = useMemo(() => cert ? [
    { label: "Authenticity", pass: cert.authenticity_pass },
    { label: "Quality & construction", pass: cert.quality_pass },
    { label: "Size & fit", pass: cert.size_fit_pass },
  ] : [], [cert]);
  const passedCount = checks.filter((c) => c.pass).length;

  if (!p) return <div className="p-24 font-mono text-sm">Loading...</div>;

  const colourGallery = p.colour_images?.[colour] || [];
  const gallery = colourGallery.length ? colourGallery : (p.images || []);
  const isFootwear = p.category === "footwear";
  const reviewCount = Number(p.review_count) || 0;
  const certNo = `SP-${(p.id || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase()}`;
  const certDate = cert?.certified_at ? new Date(cert.certified_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase() : null;
  const dims = [p.length_cm, p.width_cm, p.height_cm].some(Boolean)
    ? `${p.length_cm || "–"} × ${p.width_cm || "–"} × ${p.height_cm || "–"} cm` : "";

  const pickColour = (c) => { setColour(c); setImg(0); };
  const jump = (sid) => { sectionRefs.current[sid]?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const checkDelivery = (e) => {
    e?.preventDefault();
    const est = estimateDelivery(pin);
    setDelivery(est);
    if (est) { try { localStorage.setItem("ts_pincode", pin.replace(/\D/g, "")); } catch {} }
  };
  const addToCart = async () => {
    // Guests can add to a browser-local bag; they verify their mobile at checkout.
    if (user && user.role !== "buyer") { toast.error("Only buyers can shop. Log in as a buyer."); return; }
    if (!size) { toast.error("Select a size first."); return; }
    try { await add(p.id, size, 1, p); addToCartEvent(p, 1); toast.success("Added to bag"); }
    catch (e) { toast.error("Could not add to bag"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-8 md:py-12 pb-28 md:pb-12">
      {/* Breadcrumb */}
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-8">
        <Link to="/shop" className="hover:text-[hsl(var(--accent))]">Shop</Link>
        <span className="mx-2 text-foreground/30">/</span>
        <Link to={`/shop?category=${p.category}`} className="hover:text-[hsl(var(--accent))]">{p.category_name || p.category}</Link>
        <span className="mx-2 text-foreground/30">/</span>
        <span className="text-foreground/60">{p.title}</span>
      </p>

      {/* HERO */}
      <div className="grid md:grid-cols-12 gap-10 lg:gap-14">
        {/* Gallery */}
        <div className="md:col-span-7 min-w-0">
          <div className="grid grid-cols-[72px_1fr] gap-4">
            <div className="flex flex-col gap-3 min-w-0">
              {gallery.map((src, i) => (
                <button key={i} onClick={() => setImg(i)} data-testid={`thumb-${i}`}
                  className={`aspect-square border overflow-hidden transition-colors ${img === i ? "border-foreground" : "border-transparent hover:border-foreground/40"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="aspect-[4/5] bg-secondary relative overflow-hidden min-w-0">
              {gallery[img] && <img src={gallery[img]} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />}
              {cert?.passed && (
                <span className="absolute top-4 left-4 stamp" style={{ borderColor: "hsl(var(--accent))", color: "hsl(var(--accent))" }}>
                  ✓ SPOTTED
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Buy column */}
        <div className="md:col-span-5">
          <Link to={`/vendor/${p.vendor_slug}`} className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-[hsl(var(--accent))]">
            {p.vendor_name}
          </Link>
          <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mt-2 tracking-tighter">{p.title}</h1>

          {/* Signals */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5">
            {cert?.passed && (
              <span className="inline-flex items-center gap-1.5 border border-[hsl(var(--accent))] text-[hsl(var(--accent))] px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest">
                <Check size={11} strokeWidth={3} /> Spotter Certified
              </span>
            )}
            {p.rating > 0 && (
              <button onClick={() => jump("reviews")} className="font-mono text-[11px] text-muted-foreground hover:text-foreground">
                ★ {Number(p.rating).toFixed(1)}{reviewCount ? ` · ${reviewCount} review${reviewCount > 1 ? "s" : ""}` : ""}
              </button>
            )}
            {isFootwear && (
              <button onClick={() => jump("sizefit")} className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">
                ◆ BIS Size Match
              </button>
            )}
          </div>

          {/* Price */}
          <div className="mt-6 flex items-baseline gap-3">
            <p className="font-mono text-2xl">₹ {p.price.toLocaleString("en-IN")}</p>
            {p.mrp > p.price && (
              <>
                <span className="font-mono text-sm text-muted-foreground line-through">₹ {Number(p.mrp).toLocaleString("en-IN")}</span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-[hsl(var(--accent))]">{Math.round((1 - p.price / p.mrp) * 100)}% off</span>
              </>
            )}
          </div>
          <p className="text-[11px] font-mono text-muted-foreground mt-1">
            Inclusive of {p.gst_rate ? `${p.gst_rate}% ` : ""}GST · Free shipping over ₹2,000
          </p>

          {/* Why we spotted it */}
          <div className="mt-7 border-l-2 border-[hsl(var(--accent))] pl-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Why we spotted it</p>
            <p className="mt-2 leading-relaxed text-[15px]">{p.description}</p>
          </div>

          {/* Colour */}
          {p.colors?.length > 0 && (
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-widest mb-3">
                Colour{colour ? <span className="text-muted-foreground"> · {colour}</span> : null}
              </p>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button key={c} data-testid={`colour-${c}`} onClick={() => pickColour(c)}
                    className={`border px-4 py-2 font-mono text-xs uppercase transition-colors ${colour === c ? "bg-foreground text-background border-foreground" : "border-foreground/40 hover:border-foreground"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-widest">Select Size{p.size_type ? <span className="text-muted-foreground"> · {p.size_type}</span> : null}</p>
              <button onClick={() => (chartKindFor(p.category) ? setSizeGuideOpen(true) : jump("sizefit"))} className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] flex items-center gap-1">
                <Ruler size={12} /> Size guide
              </button>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
              {p.sizes.map((s) => (
                <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                  className={`border py-3 font-mono text-xs uppercase transition-colors ${size === s ? "bg-foreground text-background border-foreground" : "border-foreground/40 hover:border-foreground"}`}>
                  {s}
                </button>
              ))}
            </div>
            {isFootwear && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] flex items-center gap-1.5">
                <Check size={12} strokeWidth={3} /> BIS size match verified for this pair
              </p>
            )}
          </div>

          {/* Delivery */}
          <div className="mt-8 border border-foreground/25 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Delivery</p>
            <form onSubmit={checkDelivery} className="mt-3 flex gap-2">
              <input value={pin} onChange={(e) => { setPin(e.target.value); setDelivery(null); }} inputMode="numeric" maxLength={6}
                placeholder="Enter pincode" data-testid="pincode-input"
                className="flex-1 border border-foreground/40 bg-background px-3 py-2.5 font-mono text-sm tracking-widest focus:border-foreground outline-none" />
              <button type="submit" className="border border-foreground px-4 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
                Check
              </button>
            </form>
            {delivery && (
              <div className="mt-3 text-sm flex items-start gap-2">
                <Check size={15} strokeWidth={2.5} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                <span>
                  <span className="text-[hsl(var(--accent))] font-mono text-[11px] uppercase tracking-widest">Delivery available</span>
                  <span className="block text-muted-foreground mt-0.5">Estimated {delivery.min}–{delivery.max} days · {delivery.cod ? "Cash on delivery available" : "Prepaid only"}</span>
                </span>
              </div>
            )}
            {delivery === null && pin && pin.replace(/\D/g, "").length !== 6 && (
              <p className="mt-2 text-[11px] font-mono text-muted-foreground">Enter a 6-digit pincode.</p>
            )}
          </div>

          {/* CTAs */}
          <button data-testid="add-to-cart-btn" onClick={addToCart}
            className="w-full mt-6 bg-foreground text-background py-5 font-mono text-xs uppercase tracking-[0.24em] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] transition-colors flex items-center justify-center gap-2">
            Add to Bag <ArrowRight size={15} />
          </button>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <WishlistButton productId={p.id} className="w-full h-12" />
            <button data-testid="ask-about-product" onClick={() => openFor({ id: p.id, title: p.title })}
              className="h-12 border border-foreground/40 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-foreground transition-colors flex items-center justify-center gap-2">
              <MessageCircle size={14} strokeWidth={1.5} /> Ask a Spotter
            </button>
          </div>

          {/* Certification card */}
          {cert?.passed && (
            <div className="mt-8 border border-foreground p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Certificate № {certNo}</p>
                  <p className="font-serif text-2xl mt-1 tracking-tight">Spotter Certified</p>
                </div>
                <span className="w-11 h-11 rounded-full border-2 border-[hsl(var(--accent))] text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                  <Check size={22} strokeWidth={2.5} />
                </span>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] mt-4">
                Inspection passed{certDate ? ` · ${certDate}` : ""}
              </p>
              <p className="font-mono text-[11px] uppercase tracking-widest mt-1">{passedCount} / {checks.length} checks passed</p>
              <div className="mt-4 space-y-2">
                {checks.map((c) => (
                  <div key={c.label} className="flex items-center gap-2.5 text-sm">
                    <Check size={14} strokeWidth={2.5} className={c.pass ? "text-[hsl(var(--accent))]" : "text-muted-foreground"} />
                    <span>{c.label}</span>
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.pass ? "Passed" : "—"}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => jump("inspection")} className="mt-5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] flex items-center gap-1.5 hover:gap-2.5 transition-all">
                View inspection <ArrowRight size={13} />
              </button>
              <p className="mt-4 pt-4 border-t border-foreground/15 text-[11px] text-muted-foreground leading-relaxed">
                Inspected against applicable certification criteria before listing. Not a warranty of future performance.
              </p>
            </div>
          )}

          {/* Trust strip */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="border border-foreground/15 py-4 px-2">
              <Truck size={16} className="mx-auto text-[hsl(var(--accent))]" />
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Ships in {estimateDelivery(pin)?.max || 8} days</p>
            </div>
            <div className="border border-foreground/15 py-4 px-2">
              <RotateCcw size={16} className="mx-auto text-[hsl(var(--accent))]" />
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{p.returns_accepted ? `${p.return_window_days}-day returns` : "No returns"}</p>
            </div>
            <div className="border border-foreground/15 py-4 px-2">
              <ShieldCheck size={16} className="mx-auto text-[hsl(var(--accent))]" />
              <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{p.warranty_months ? `${p.warranty_months}-mo warranty` : "Inspected"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION RAIL + CONTENT */}
      <div className="mt-16 md:mt-24">
        <SectionRail active={active} onJump={jump} />

        <div className="max-w-3xl mx-auto md:mx-0 md:max-w-none md:grid md:grid-cols-12 md:gap-14 mt-10">
          <div className="md:col-span-8">

            {/* THE DETAILS */}
            <section ref={(el) => (sectionRefs.current.details = el)} data-section="details" className="scroll-mt-24 pb-14 border-b border-foreground/15">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The details</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">What you're getting.</h2>
              <p className="mt-4 leading-relaxed text-[15px] max-w-2xl text-muted-foreground">{p.description}</p>

              {/* Benefits You'll Feel — rich title+paragraph blocks (fall back to highlight chips) */}
              {p.benefits?.length > 0 ? (
                <div className="mt-10">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Benefits you'll feel</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-x-10 gap-y-7">
                    {p.benefits.map((b, i) => {
                      const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
                      return (
                        <div key={i} className="flex gap-3.5">
                          <Icon size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                          <div>
                            <p className="font-serif text-lg tracking-tight leading-snug">{b.title}</p>
                            {b.body && <p className="text-sm text-muted-foreground leading-relaxed mt-1">{b.body}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : p.highlights?.length > 0 ? (
                <div className="mt-10">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Benefits you'll feel</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    {p.highlights.map((h, i) => {
                      const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
                      return (
                        <div key={i} className="flex items-start gap-3 border border-foreground/15 p-4">
                          <Icon size={18} strokeWidth={1.5} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                          <p className="text-sm leading-snug">{h}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Specifications */}
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-12 mb-2">Specifications</p>
              <div>
                <Spec label="Brand">{p.brand}</Spec>
                <Spec label="Material">{p.material}</Spec>
                <Spec label="Colours">{p.colors?.join(", ")}</Spec>
                {Object.entries(p.attributes || {}).map(([k, v]) => (
                  <Spec key={k} label={k.replace(/_/g, " ")}>{Array.isArray(v) ? v.join(", ") : String(v)}</Spec>
                ))}
                <Spec label="Dimensions">{dims}</Spec>
                <Spec label="Weight">{p.weight_g ? `${p.weight_g} g` : ""}</Spec>
                <Spec label="Care">{p.care}</Spec>
                <Spec label="Country of origin">{p.country_of_origin}</Spec>
                <Spec label="Warranty">{p.warranty || (p.warranty_months ? `${p.warranty_months} months` : "")}</Spec>
              </div>
            </section>

            {/* SIZE & FIT */}
            <section ref={(el) => (sectionRefs.current.sizefit = el)} data-section="sizefit" className="scroll-mt-24 py-14 border-b border-foreground/15">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Size &amp; fit</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">Get the size right.</h2>
              <p className="mt-4 leading-relaxed text-[15px] max-w-xl">
                {isFootwear
                  ? "Every pair is measured against BIS reference lasts, so the size you order is the size that fits. Available in " + (p.sizes || []).join(", ") + (p.size_type ? ` (${p.size_type})` : "") + "."
                  : "Available in " + (p.sizes || []).join(", ") + (p.size_type ? ` (${p.size_type})` : "") + ". Measured against our reference sample during inspection."}
              </p>
              <Link to="/size-match" className="mt-6 inline-flex items-center gap-2 border border-foreground px-5 py-3 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
                <Ruler size={14} /> Find my BIS size match <ArrowRight size={13} />
              </Link>
            </section>

            {/* INSPECTION */}
            <section ref={(el) => (sectionRefs.current.inspection = el)} data-section="inspection" className="scroll-mt-24 py-14 border-b border-foreground/15">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Inspection</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">The Spotter report.</h2>
              {cert?.passed ? (
                <>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-[hsl(var(--accent))] mt-4">
                    Certificate № {certNo} · Passed{certDate ? ` · ${certDate}` : ""} · {passedCount}/{checks.length} checks
                  </p>
                  <div className="mt-6 space-y-0">
                    {[
                      { label: "Authenticity", pass: cert.authenticity_pass, q: "Genuine materials, verified source.", notes: cert.skeptic_notes },
                      { label: "Quality & construction", pass: cert.quality_pass, q: "Stitching, finish, materials — zero-defect standard.", notes: cert.perfectionist_notes },
                      { label: "Size & fit", pass: cert.size_fit_pass, q: "Sized true, wears the way it should.", notes: cert.softie_notes },
                    ].map((r, i) => (
                      <div key={i} className="grid grid-cols-[auto_1fr_auto] gap-5 py-5 border-b border-foreground/12 items-start">
                        <span className={`w-9 h-9 border-2 flex items-center justify-center shrink-0 ${r.pass ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-foreground/40 text-muted-foreground"}`}>
                          <Check size={18} strokeWidth={2.5} />
                        </span>
                        <div>
                          <p className="font-serif text-xl tracking-tight">{r.label}</p>
                          <p className="text-xs text-muted-foreground italic mt-0.5">{r.q}</p>
                          {r.notes && <p className="text-sm mt-2 leading-relaxed">{r.notes}</p>}
                        </div>
                        <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border shrink-0 ${r.pass ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-foreground/40 text-muted-foreground"}`}>
                          {r.pass ? "Pass" : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-5">
                    Verified against reference sample before listing
                  </p>
                </>
              ) : (
                <p className="mt-4 text-muted-foreground">This product is awaiting inspection. It will show its full Spotter report once certified.</p>
              )}
            </section>

            {/* REVIEWS */}
            <section ref={(el) => (sectionRefs.current.reviews = el)} data-section="reviews" className="scroll-mt-24 py-14 border-b border-foreground/15">
              <Reviews productId={p.id} />
            </section>

            {/* THE MAKER */}
            <section ref={(el) => (sectionRefs.current.maker = el)} data-section="maker" className="scroll-mt-24 py-14 border-b border-foreground/15">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The maker</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">{p.vendor_name}</h2>
              <p className="mt-4 leading-relaxed text-[15px] max-w-xl text-muted-foreground">
                A verified seller on The Spotters. Every listing from {p.vendor_name} is inspected against a reference sample before it goes live.
              </p>

              {/* Quick facts */}
              <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
                {p.country_of_origin && (
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Country of origin</p>
                    <p className="text-sm mt-1">{p.country_of_origin}</p>
                  </div>
                )}
                {cert?.passed && (
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Inspection</p>
                    <p className="text-sm mt-1 text-[hsl(var(--accent))]">Certified · {passedCount}/{checks.length} checks</p>
                  </div>
                )}
                {p.warranty_months > 0 && (
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Warranty</p>
                    <p className="text-sm mt-1">{p.warranty_months} months</p>
                  </div>
                )}
              </div>

              <Link to={`/vendor/${p.vendor_slug}`} className="mt-7 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] hover:gap-3 transition-all">
                Visit {p.vendor_name} <ArrowRight size={13} />
              </Link>

              {/* Manufacturer & compliance — India Legal Metrology disclosure */}
              <div className="mt-10 border border-foreground/15 p-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Manufacturer &amp; compliance</p>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 text-sm">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Marketed by</p>
                    <p className="mt-1">{p.vendor_name}</p>
                  </div>
                  {(p.manufacturer || p.manufacturer_address) && (
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Manufactured / packed by</p>
                      <p className="mt-1 leading-relaxed">{p.manufacturer || p.vendor_name}{p.manufacturer_address ? <><br />{p.manufacturer_address}</> : null}</p>
                    </div>
                  )}
                  {p.country_of_origin && (
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Country of origin</p>
                      <p className="mt-1">{p.country_of_origin}</p>
                    </div>
                  )}
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Net price</p>
                    <p className="mt-1">₹ {Number(p.mrp || p.price).toLocaleString("en-IN")} (MRP, inclusive of all taxes)</p>
                  </div>
                  <div className="sm:col-span-2 pt-4 border-t border-foreground/12">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Consumer care</p>
                    <p className="mt-1 leading-relaxed text-muted-foreground">
                      For complaints or assistance, contact {COMPANY.brand} —{" "}
                      <a href={`mailto:${COMPANY.email}`} className="text-foreground hover:text-[hsl(var(--accent))]">{COMPANY.email}</a>
                      {" · "}{COMPANY.phone}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* SHIPPING & RETURNS */}
            <section ref={(el) => (sectionRefs.current.shipping = el)} data-section="shipping" className="scroll-mt-24 py-14">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Shipping &amp; returns</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">How it reaches you.</h2>
              <div className="mt-6 max-w-xl">
                <Spec label="Shipping">Dispatched by {p.vendor_name} via The Spotters' logistics partner. Estimated 5–8 days.</Spec>
                <Spec label="Returns">{p.returns_accepted ? `${p.return_window_days}-day returns${p.replacement_available ? " · replacement available" : ""}.` : "This item is not eligible for return."}</Spec>
                <Spec label="Warranty">{p.warranty || (p.warranty_months ? `${p.warranty_months} months` : "Not specified")}</Spec>
                {p.return_policy && (
                  <div className="mt-5 text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{p.return_policy}</div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Also spotted */}
      {related.length > 0 && (
        <div className="mt-8 pt-14 border-t border-foreground/15">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Also spotted</p>
              <h2 className="font-serif text-4xl tracking-tighter mt-1">More like this.</h2>
            </div>
            <Link to={`/shop?category=${p.category}`} className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] hidden sm:flex items-center gap-1.5 hover:gap-2.5 transition-all">
              See all {p.category_name || p.category} <ArrowRight size={13} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={`/product/${r.id}`} className="group">
                <div className="aspect-[4/5] bg-secondary overflow-hidden">
                  {r.images?.[0] && <img src={r.images[0]} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{r.vendor_name}</p>
                <p className="font-serif text-lg tracking-tight leading-tight mt-0.5">{r.title}</p>
                <p className="font-mono text-sm mt-1">₹ {Number(r.price).toLocaleString("en-IN")}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile sticky Add-to-Bag */}
      <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-background border-t border-foreground/20 px-5 py-3 flex items-center gap-4">
        <div className="shrink-0">
          <p className="font-mono text-sm">₹ {p.price.toLocaleString("en-IN")}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{size ? `Size ${size}` : "Select size"}</p>
        </div>
        <button onClick={addToCart} className="flex-1 bg-foreground text-background py-3.5 font-mono text-[11px] uppercase tracking-[0.2em]">
          Add to Bag
        </button>
      </div>

      <SizeGuide open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} category={p.category} defaultGender={genderFromDepartment(p.department)} />
    </div>
  );
}
